import api from "./api";
import { toManualPaymentFormData } from "./paymentService";

// Facility booking directory (system-user / RBAC — gated by "view_bookings"
// and "view_booking_details" on the backend). Booking creation is a
// member-only flow (POST /api/bookings requires a MemberRegistration token),
// and there is no admin-facing approve/reject/cancel/complete endpoint despite
// those permissions being seeded — so this page is intentionally view-only.

// GET /api/bookings — optional { member_id, date } filters (no free-text
// search on this endpoint). Returns a plain array of bookings: {id,
// facility_id, total_entries, total_slots, total_fee, booking_date (null —
// a multi-entry booking has no single date, see each entry's own
// booking_date instead), payment_id, payment_reference, payable_amount,
// paid_amount, payment_status_id, payment_status, paid_at, member_id,
// member_code, title, initials, lastname, email, created_at, updated_at}.
export const listBookings = (params = {}) =>
  api.get("/api/bookings", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/bookings/{id} — flat object with the same fields plus `entries`
// (one row per booked session: {id, facility_slot_id, slot_code,
// booking_date, start_time, end_time, total_slots, entry_fee, use_light,
// light_slot_count} — each entry carries its own date, since a single
// booking can now span up to 10 differently-dated sessions). A legacy
// `slots` field (per facility_booking row) is also present alongside
// `entries` — prefer `entries` for anything new.
export const getBooking = (id) => api.get(`/api/bookings/${id}`).then((r) => r.data);

// GET /api/members/{memberCode}/bookings — gated by "view_bookings", driven
// from the member detail page (MembersManagement.jsx). `memberCode` must be
// the public "MEM000502" form — the route itself is regex-constrained to
// MEM[0-9]+ (routes/api.php), a raw numeric id 404s before ever reaching
// BookingController::forMember. Response wraps the member's own bookings
// under a `bookings` key alongside their identity: { member_id, member_code,
// member_name, email, primary_phone_number, club_id, club_code, bookings:
// [...] } — same row shape as listBookings() above, just pre-filtered to one
// member; this unwraps straight to that array.
export const getMemberBookings = (memberCode) =>
  api.get(`/api/members/${memberCode}/bookings`).then((r) => r.data?.bookings || []);

// ── Member-facing booking flow (public preview/status, member-token create/mine) ──
// Multi-entry model: a single checkout bundles 1-10 dated time-slot
// "entries", all on the same facility, into one booking. Rules enforced
// server-side (MultiEntryBookingService): 1-10 entries; every entry's
// facility_slot must belong to the same facility_id; each entry's duration
// (minutes(start_time,end_time)) must be >=60 and a multiple of 30; member
// booking dates must be <=30 days out. There is no `use_light` request
// field anymore — lighting is entirely server-computed per entry now
// (previously a staff-only manual override; that override is gone too).

// Normalizes an entry from either shape the backend returns entries in:
// preview responses nest the slot as `facility_slot: {id, slot_code}`,
// while confirm/detail/conflict responses put it flat as
// `facility_slot_id`/`slot_code` directly on the entry. Use this wherever
// an entry (from any of those three response types) needs its slot id/code
// read, so callers don't have to special-case the shape themselves.
export const normalizeEntry = (e) => ({
  ...e,
  facility_slot_id: e.facility_slot_id ?? e.facility_slot?.id ?? null,
  slot_code: e.slot_code ?? e.facility_slot?.slot_code ?? null,
});

// POST /api/bookings/preview — public, no DB write. Same body as
// createBooking: { facility_id, entries: [{facility_slot_id, booking_date,
// start_time, end_time}, ...] } (1-10 entries, start_time/end_time as
// "H:i"). A flat single-entry shape (facility_slot_id/booking_date/
// start_time/end_time at the top level, no `entries` key) is also accepted
// and auto-wrapped into a one-entry array server-side, but new callers
// should always send `entries`. Returns { audience, facility: {id,
// facility_name, light_required_from}, total_entries, max_entries (10),
// max_booking_horizon_days, latest_booking_date, total_slots, use_light
// (aggregate — true if any entry is light-priced), use_light_auto (always
// true), total_fee, entries: [{entry_index, facility_slot: {id, slot_code},
// booking_date, start_time, end_time, total_slots, use_light,
// light_slot_count, light_required_from, entry_fee}] }. Each entry's own
// light_slot_count (out of its total_slots) is how many of that entry's
// slots landed light-priced when its range straddles the facility's
// light_required_from cutoff.
export const previewBooking = (payload) =>
  api.post("/api/bookings/preview", payload).then((r) => r.data);

// POST /api/bookings (also aliased as POST /api/bookings/confirm — same
// controller method) — requires a member token. Same { facility_id,
// entries: [...] } body as previewBooking. Locks every entry's slots for a
// single 10-minute window covering the whole booking (lock_expires_in_minutes)
// and returns { payment_reference, external_payment_reference,
// payable_amount, payment_payload, booking: {id, facility_id, total_entries,
// total_slots, total_fee, payment_id, entries: [{id, facility_slot_id,
// slot_code, booking_date, start_time, end_time, total_slots, entry_fee,
// use_light, light_slot_count}]} }. payment_payload is built the same way
// as registration/reprints (PaymentGatewayPayload::make) — pass it straight
// to openPaymentGateway. Confirm is all-or-nothing: if any entry conflicts,
// the whole request 409s (or 422s for a mixed-facility payload) and nothing
// is created — see the conflict shapes below.
//
// Conflict responses (nothing created, safe to resubmit after fixing the
// listed entries): 409 { success:false, error_code:
// "booking_entries_unavailable" | "booking_entry_blocked", message, phase,
// conflict_count, total_entries, booking_created:false, action, conflicts:
// [{entry_index, reason: "slots_unavailable" | "admin_block", reason_label,
// entry: {entry_index, facility_slot_id, slot_code, booking_date,
// start_time, end_time}, unavailable_slot_count?, unavailable_slots?,
// unavailable_slot_ids?, user_message}] }. "booking_entries_unavailable" can
// list multiple conflicting entries; "booking_entry_blocked" (an
// admin-blocked period) always reports exactly one, even if other entries
// are also blocked. A 422 with error_code "mixed_facilities" means an entry
// didn't belong to the same facility as the rest — shouldn't happen if the
// caller only ever builds a single-facility entries array.
export const createBooking = (payload) =>
  api.post("/api/bookings", payload).then((r) => r.data);

// GET /api/member/bookings — the logged-in member's own bookings. One flat
// row per BOOKING (not per entry/slot) — a booking can now have
// total_entries > 1, each on its own date. The row's own facility_name/
// slot_code/start_time/end_time are just a representative first entry, not
// the full breakdown — fetch getMyBooking(id) for the complete entries[] list.
// No payment_payload on this endpoint, so a Pending row here can't reopen
// the real gateway popup (only the create response carries it).
export const getMyBookings = () =>
  api.get("/api/member/bookings").then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/member/bookings/{id} — same detail shape as getBooking() above
// (BookingController::showMine reuses the identical bookingDetails() builder
// as the staff/admin route), but member-token-gated and ownership-checked:
// 404s with "Booking not found." if the id doesn't belong to the requesting
// member, rather than the 403 a member token gets from the staff route. Use
// this (not getBooking()) for a member/coach's own "View Details" — the
// plain /api/bookings/{id} route is staff/admin-only.
export const getMyBooking = (id) => api.get(`/api/member/bookings/${id}`).then((r) => r.data);

// POST /api/booking-payments/status — public. Echo back the exact
// payment_reference + booking_id createBooking returned. Auto-expires an
// overdue pending lock the moment it's polled (slots -> Expired, payment ->
// cancelled). Returns { payment_status, payment_completed, booking: {...,
// entries:[...], slots:[{booking_status,...}]} } — no payment_payload here
// either.
export const checkBookingPaymentStatus = ({ payment_reference, booking_id }) =>
  api.post("/api/booking-payments/status", { payment_reference, booking_id }).then((r) => r.data);

// ── Staff booking (system-user books on behalf of a member) ────────────────
// A second, independent creation path from createBooking above — gated by
// "create_bookings" (Instructor/Bursar/Director/Admin), not a member token.
// Same multi-entry model and per-entry rules as member self-booking (1-10
// entries, all one facility, each >=60min in 30min steps), except: dates may
// be up to 90 days out (not 30), and there is NO flat-shape fallback here —
// { member_id | member_code | email, facility_id, entries: [...] } must always be
// sent as a real `entries` array, even for a single session.

// GET /api/staff/coach-clubs?member_id|member_code|email= — clubs the given
// member coaches for (create_bookings, staff token). Returns
// { member_id, member_code, clubs: [{ id, club_code, club_name }] }; clubs is
// empty for a non-coach. When it isn't empty, both previewStaffBooking() and
// createStaffBooking() require a club_id from this list (422 "Select the
// club this coach is booking for." otherwise) — staff never fall back to the
// coach's active club the way a portal booking does.
export const listStaffCoachClubs = (memberPayload) =>
  api.get("/api/staff/coach-clubs", { params: memberPayload }).then((r) => r.data);

// POST /api/staff/bookings/preview — no DB write. Same body as
// createStaffBooking — the member is required here too (422 "Provide
// member_id, member_code, or email for staff bookings." without one), and a
// member_code/email that doesn't resolve 422s/404s as well. Response shape
// matches previewBooking() above (entries nested as facility_slot:
// {id, slot_code}), plus reflects the 90-day staff horizon in
// max_booking_horizon_days/latest_booking_date.
export const previewStaffBooking = (payload) =>
  api.post("/api/staff/bookings/preview", payload).then((r) => r.data);

// POST /api/staff/bookings — { member_id | member_code | email, facility_id,
// entries: [...] }. member_code is the public "MEM000123" form (case-
// insensitive); email is matched case-insensitively too — both 404/422 if
// they don't resolve to a member, even during preview (which requires a
// member just like create does). Locks
// every entry's slots until midnight tonight (not
// a fixed number of minutes — the lock spans from creation to the start of
// the next calendar day) since the member isn't present to pay immediately.
// Returns { message, lock_expires_in_minutes, created_by_staff: true,
// payment_reference, external_payment_reference, payable_amount,
// payment_payload: null, booking: {..., entries: [...]} } — same envelope
// and entries[] shape as createBooking()'s response. payment_payload is
// always null here — staff bookings are shroff/manual-confirm only by
// backend design (manualConfirm below explicitly rejects a booking with no
// created_by_user_id), so there is no gateway popup to open for this flow.
// Confirm is all-or-nothing with the same conflict shapes documented on
// createBooking() above.
export const createStaffBooking = (payload) => api.post("/api/staff/bookings", payload).then((r) => r.data);

// GET /api/booking-payments/manual-search?payment_reference=… — look up a
// staff-created booking payment by its reference, so staff can resume the
// shroff confirm step later from just the reference printed on the voucher,
// without needing the exact response object createStaffBooking() returned
// at creation time. Gated by "view_payments". Mirrors
// searchMemberRegistrationPayment/searchClubRegistrationPayment in
// paymentService.js. Runs the same lock-expiry check completeBooking() does
// (see manualConfirm below), so a search against a lapsed staff lock
// reflects the correct (Expired/Cancelled) status rather than a stale
// "Pending". 404s with { success:false, message } if the reference doesn't
// match a booking payment, or the payment has no linked booking.
// Response payment object (BookingController::formatBookingPaymentRow):
// { payment_type: "booking", payment_id, booking_id, income_id,
// income_description, ledger_code, payment_reference,
// external_payment_reference, payable_amount, paid_amount,
// payment_status_id, payment_status, payment_completed, paid_at,
// created_by_staff, manual_payment_allowed (== created_by_staff — false
// means it's a member self-booking, which manualConfirm below will reject),
// pending_manual_approval, pending_approval_id, member_id, member_code,
// payer: {title, initials, lastname, full_name, email} }.
export const searchBookingPayment = (paymentReference) =>
  api
    .get("/api/booking-payments/manual-search", { params: { payment_reference: paymentReference } })
    .then((r) => r.data.payment);

// POST /api/booking-payments/manual-confirm — multipart. payload:
// { payment_reference (required — from createStaffBooking's response),
// paid_amount, paid_date, receipt_reference, receipt (File, required), note? }. Gated by
// "submit_manual_payment_receipts". 422s if the booking wasn't staff-created
// ("Member self-bookings must be paid online. Shroff / manual payment is
// only allowed for staff-created bookings."), and 422s separately if the
// staff lock (until midnight tonight) has already elapsed by confirm time
// ("Booking lock expired. Please create a new booking.") — this re-check
// happens lazily inside completeBooking() at confirm time, not on a
// background timer, and itself flips the booking to Expired/payment to
// Cancelled when it fires. Marks the booking Paid
// immediately (HTTP 200, not a queued 202) — a manual_payment_receipts row
// is still queued for Director/Admin acknowledgement on the Payments ->
// Manual Approvals tab, but that step is audit-only now and doesn't gate
// the payment. Response mirrors the other manual-confirm endpoints:
// { message: "Booking payment confirmed. Receipt pending director
// acknowledgement.", payment_method: "Shroff Payment", receipt_reference,
// approval_id, approval_status: "Pending", ...booking/payment fields }.
export const confirmBookingManualPayment = (payload) =>
  api.post("/api/booking-payments/manual-confirm", toManualPaymentFormData(payload)).then((r) => r.data);

// GET /api/booking-payments/{paymentId}/shroff-voucher?payment_reference=...
// — printable pay-at-counter voucher PDF for a staff-created booking, keyed
// by the booking's payment id (createStaffBooking's response carries this at
// booking.payment_id) plus the same payment_reference as a required query
// param (ApplicantShroffVoucherController::booking). Gated by "view_payments".
// responseType "blob" so axios doesn't try to JSON-parse the PDF.
export const getBookingShroffVoucherPdf = (paymentId, paymentReference) =>
  api
    .get(`/api/booking-payments/${paymentId}/shroff-voucher`, {
      params: { payment_reference: paymentReference },
      responseType: "blob",
    })
    .then((r) => r.data);
